import type { SimulationState, ThemeData } from '../types';
import type { GameEvent } from '../events/GameEvents';
import type { SeededRandom } from '../rng/SeededRandom';

export function trainingSystemUpdate(
  state: SimulationState,
  themeData: ThemeData,
  rng: SeededRandom,
): GameEvent[] {
  const events: GameEvent[] = [];
  const cooldownTicks =
    themeData.balance.trainingCooldownWeeks * themeData.balance.ticksPerDay * themeData.balance.daysPerWeek;

  for (const staff of state.staff) {
    // Tick down cooldown
    if (staff.trainCooldownTicks > 0) {
      staff.trainCooldownTicks--;
    }

    // Tick down equipment bonus
    if (staff.equipmentTicksRemaining > 0) {
      staff.equipmentTicksRemaining--;
      if (staff.equipmentTicksRemaining <= 0) {
        staff.equipmentBonus = 1.0;
      }
    }

    if (!staff.isTraining) continue;

    staff.trainingTicksRemaining--;
    // Morale boost during training
    staff.morale = Math.min(100, staff.morale + themeData.balance.trainingSatisfactionBonus / 70); // Spread over training duration

    if (staff.trainingTicksRemaining <= 0) {
      staff.isTraining = false;
      staff.trainCooldownTicks = cooldownTicks;

      // Find the training definition
      const training = themeData.entities.trainingOptions.find(
        (t) => t.targetStat === staff.trainingTarget,
      );

      if (training) {
        // Stat gain with diminishing returns
        const currentStat = staff.stats[training.targetStat] ?? 0;
        const effectiveGain = Math.round(training.statGain * (1 - currentStat / 200));
        const actualGain = Math.max(1, effectiveGain);
        staff.stats[training.targetStat] = currentStat + actualGain;

        // Trait learning chance
        let traitLearned: string | null = null;
        if (training.possibleTraits.length > 0 && rng.nextBool(training.traitChance)) {
          const available = training.possibleTraits.filter(
            (t) => !staff.traits.includes(t) && staff.traits.length < 3,
          );
          if (available.length > 0) {
            traitLearned = rng.pick(available);
            staff.traits.push(traitLearned);
          }
        }

        events.push({
          type: 'TRAINING_COMPLETED',
          staffId: staff.id,
          name: staff.name,
          stat: training.targetStat,
          gain: actualGain,
          traitLearned,
        });
      }

      staff.trainingTarget = null;
    }
  }

  return events;
}
